// The two things a tree is judged on, per fit tier: recall (how much of the
// conversion volume the good tiers hold) and precision (how well each tier
// actually converts). The arithmetic lives here rather than in the chart so it
// can be tested without a DOM, and so the two charts cannot disagree about what
// a tier contains.
//
// Scope is THIS ONE TREE, deliberately. On a weighted ensemble the deployed
// tiers cut the weighted sum of every tree's node rate, so a single tree's
// tiers are not exactly what ships. The tree being edited is what the page is
// about, and the charts carry the tree number in a heading precisely because
// the diagnostics panel beside them reports across the whole model: the two
// scopes sit next to each other and the seam has to be readable.

import { EnsembleThreshold } from '../../../models/ensembling';
import { Threshold } from '../../../models/ModelPerformances';
import {
  nodeSegment,
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from './nodeSegment';

/** Best fit first, the order every other fit surface stacks them in. */
export const TIERS: Threshold[] = ['very good', 'good', 'medium', 'low'];

/** The parts of a tree node this module reads. */
export interface TreeLeaf {
  population?: number;
  conversions?: number;
  children?: TreeLeaf[];
}

export interface TierPerformance {
  segment: Threshold;
  label: string;
  color: string | null;
  /** Text drawn ON `color`. Never white by default: see nodeSegment's ON_FILL. */
  textColor: string | null;
  population: number;
  conversions: number;
  /** Percent of the tree's leads that land in this tier, 0 to 100. */
  leadShare: number;
  /** Percent of the tree's conversions that land in this tier, 0 to 100. */
  conversionShare: number;
  /** Conversion rate within the tier, as a percentage, 0 to 100. */
  conversionRate: number;
  /**
   * Bar length as a percent of the track, scaled so the best converting tier
   * fills it. Scaling against a flat 0 to 100 axis instead would draw every bar
   * as a sliver on the low base rates most fit models actually run at, which is
   * the case the chart exists to make readable.
   */
  barPercent: number;
}

export interface TreeTierPerformance {
  /** Always four, best to worst, whether or not the tree has a leaf in each. */
  tiers: TierPerformance[];
  population: number;
  conversions: number;
}

/**
 * Every leaf under a node, in tree order. A node with no children is a leaf,
 * which is the same test the node card uses to decide which shape to draw.
 */
export function leavesOf(node?: TreeLeaf | null): TreeLeaf[] {
  if (!node) return [];
  if (!node.children?.length) return [node];
  return node.children.reduce<TreeLeaf[]>(
    (leaves, child) => leaves.concat(leavesOf(child)),
    []
  );
}

/**
 * The tree's leaves rolled up into the four fit tiers.
 *
 * Null when the model has no thresholds configured. That is not a formality:
 * `nodeSegment` reads an all-zero threshold set as "no segments" rather than
 * putting every leaf in Very Good, and a chart that claimed a tree was entirely
 * Very Good would be worse than no chart at all.
 */
export function treeTierPerformance(
  leaves: TreeLeaf[],
  thresholds?: EnsembleThreshold
): TreeTierPerformance | null {
  // A rate of 1 clears any configured threshold, so this asks "are there
  // thresholds at all" using the same guard that colours the nodes.
  if (!nodeSegment(1, thresholds)) return null;

  // Seeded with all four tiers up front. Building this from the leaves would
  // drop a tier the tree has no leaf in, and the number of bars would then
  // change as the user moves between trees, which reads as a bug rather than as
  // an empty tier.
  const totals: Record<Threshold, { population: number; conversions: number }> =
    {
      'very good': { population: 0, conversions: 0 },
      good: { population: 0, conversions: 0 },
      medium: { population: 0, conversions: 0 },
      low: { population: 0, conversions: 0 },
    };

  let population = 0;
  let conversions = 0;

  (leaves ?? []).forEach((leaf) => {
    const leafPopulation = Number(leaf?.population) || 0;
    const leafConversions = Number(leaf?.conversions) || 0;
    // An empty leaf has no rate, so it belongs to no tier. Counting it as 0%
    // would file it under Low and let a pruned branch drag that tier down with
    // leads it does not have. `nodeSegment` happens to reject the resulting
    // NaN too, but that is its guard against a bad rate, not a promise about
    // empty leaves, and this loop should not need it read to be understood.
    if (leafPopulation <= 0) return;

    const segment = nodeSegment(leafConversions / leafPopulation, thresholds);
    if (!segment) return;

    // Several leaves in the same tier are one band, not one band each: the
    // charts describe tiers, and a tier is the union of its leaves.
    totals[segment].population += leafPopulation;
    totals[segment].conversions += leafConversions;
    population += leafPopulation;
    conversions += leafConversions;
  });

  const rates = TIERS.map((segment) => {
    const tier = totals[segment];
    return tier.population > 0 ? (tier.conversions / tier.population) * 100 : 0;
  });
  const bestRate = Math.max(...rates);

  const tiers = TIERS.map((segment, index) => {
    const tier = totals[segment];
    return {
      segment,
      label: SEGMENT_LABEL[segment],
      color: segmentColor(segment),
      textColor: segmentTextColor(segment),
      population: tier.population,
      conversions: tier.conversions,
      leadShare: population > 0 ? (tier.population / population) * 100 : 0,
      conversionShare:
        conversions > 0 ? (tier.conversions / conversions) * 100 : 0,
      conversionRate: rates[index],
      barPercent: bestRate > 0 ? (rates[index] / bestRate) * 100 : 0,
    };
  });

  return { tiers, population, conversions };
}
